import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  LinearProgress,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocalHospital as MedicalIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CreditCard as PaymentIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { facilityService } from "../../services/facilityService";
import { useAuth } from "../../contexts/AuthContext";

const FacilityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Data for tabs
  const [users, setUsers] = useState([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMPs, setLoadingMPs] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Pagination states
  const [usersPage, setUsersPage] = useState(0);
  const [mpsPage, setMpsPage] = useState(0);
  const [ordersPage, setOrdersPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchFacilityDetails();
  }, [id]);

  useEffect(() => {
    if (facility) {
      fetchStats();
      if (activeTab === 1) fetchUsers();
      if (activeTab === 2) fetchMedicalProfessionals();
      if (activeTab === 3) fetchOrders();
    }
  }, [facility, activeTab]);

  const fetchFacilityDetails = async () => {
    setLoading(true);
    try {
      const response = await facilityService.getFacilityById(id);
      if (response) {
        setFacility(response);
      } else {
        setError("Facility not found");
      }
    } catch (err) {
      console.error("Error fetching facility details:", err);
      setError("Failed to load facility details");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await facilityService.getFacilityStats(id);
      setStats(response);
    } catch (err) {
      console.error("Error fetching facility stats:", err);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await facilityService.getFacilityUsers(
        id,
        usersPage * rowsPerPage,
        rowsPerPage
      );
      setUsers(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching facility users:", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMedicalProfessionals = async () => {
    setLoadingMPs(true);
    try {
      const response = await facilityService.getFacilityMedicalProfessionals(
        id,
        mpsPage * rowsPerPage,
        rowsPerPage
      );
      setMedicalProfessionals(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching medical professionals:", err);
      setMedicalProfessionals([]);
    } finally {
      setLoadingMPs(false);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await facilityService.getFacilityOrders(id, {
        skip: ordersPage * rowsPerPage,
        limit: rowsPerPage,
      });
      setOrders(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching facility orders:", err);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const canEditFacility = () => {
    return isSuperAdmin || user?.facility_id === parseInt(id);
  };

  const getSubscriptionColor = (subscriptionType) => {
    const colors = {
      basic: "default",
      standard: "primary",
      premium: "secondary",
    };
    return colors[subscriptionType?.toLowerCase()] || "default";
  };

  const formatSubscription = (subscriptionType) => {
    if (!subscriptionType) return "N/A";
    return subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1);
  };

  const formatAdminLevel = (level) => {
    const levels = {
      1: "Basic",
      2: "Standard",
      3: "Advanced",
      4: "Super Admin",
    };
    return levels[level] || "Unknown";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString(); 
  };

  const maskCreditCard = (cardNumber) => {
    if (!cardNumber) return "N/A";
    return `**** **** **** ${cardNumber}`;
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !facility) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/facilities")}
        >
          Back to Facilities
        </Button>
      </Box>
    );
  }

  if (!facility) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Facility not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/facilities")}
          sx={{ mt: 2 }}
        >
          Back to Facilities
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to="/facilities"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Facilities
          </Button>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {facility.company_name || "Facility Details"}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={formatSubscription(facility.subscription_type)}
                color={getSubscriptionColor(facility.subscription_type)}
                size="small"
              />
              <Chip
                label={formatAdminLevel(facility.admin_level)}
                color="info"
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/facilities/${id}/edit`)}
          disabled={!canEditFacility()}
        >
          Edit Facility
        </Button>
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid  size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">{stats.total_users || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MedicalIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.total_medical_professionals || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Medical Professionals
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AssignmentIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.total_orders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AssignmentIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.active_orders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid  size={{ xs: 12, md: 8 }}>
          {/* Company Information */}
          <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Company Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <BusinessIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {facility.company_name || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {facility.email || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1">
                      {facility.phone_number || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fax Number
                    </Typography>
                    <Typography variant="body1">
                      {facility.fax || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Box display="flex" alignItems="flex-start" mb={2}>
                  <LocationIcon
                    sx={{ mr: 1, color: "text.secondary", mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {facility.address || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Licensing Information */}
          <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Licensing Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Business License
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {facility.business_license || "N/A"}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  NPI Number
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {facility.npi || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          {/* Subscription Information */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Subscription Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Subscription Type
              </Typography>
              <Chip
                label={formatSubscription(facility.subscription_type)}
                color={getSubscriptionColor(facility.subscription_type)}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Admin Level
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatAdminLevel(facility.admin_level)}
              </Typography>
            </Box>
          </Paper>

          {/* Billing Information */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Billing Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box mb={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <PaymentIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Credit Card
                </Typography>
              </Box>
              <Typography variant="body2">
                {maskCreditCard(facility.credit_card_last4)}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Billing Address
              </Typography>
              <Typography variant="body2">
                {facility.billing_address || "Same as company address"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Related Data */}
      <Paper elevation={3} sx={{ mt: 4, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Overview" />
          <Tab label={`Users (${stats.total_users || 0})`} />
          <Tab
            label={`Medical Professionals (${
              stats.total_medical_professionals || 0
            })`}
          />
          <Tab label={`Orders (${stats.total_orders || 0})`} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monthly Orders: {stats.monthly_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed Orders: {stats.completed_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Orders: {stats.active_orders || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid  size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Facility ID: {facility.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Created: {formatDate(facility.created_at)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {formatDate(facility.updated_at)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Users Tab */}
          {activeTab === 1 && (
            <Box>
              {loadingUsers ? (
                <LinearProgress />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length > 0 ? (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.full_name || "N/A"}</TableCell>
                            <TableCell>{user.email || "N/A"}</TableCell>
                            <TableCell>
                              <Chip label={user.role || "N/A"} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.status || "Active"}
                                color={user.is_active ? "success" : "warning"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body1" color="text.secondary">
                              No users found for this facility
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={stats.total_users || 0}
                    rowsPerPage={rowsPerPage}
                    page={usersPage}
                    onPageChange={(e, newPage) => setUsersPage(newPage)}
                    rowsPerPageOptions={[10]}
                  />
                </TableContainer>
              )}
            </Box>
          )}

          {/* Medical Professionals Tab */}
          {activeTab === 2 && (
            <Box>
              {loadingMPs ? (
                <LinearProgress />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>License Number</TableCell>
                        <TableCell>Vehicle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {medicalProfessionals.length > 0 ? (
                        medicalProfessionals.map((mp) => (
                          <TableRow key={mp.id}>
                            <TableCell>
                              <Button
                                component={Link}
                                to={`/medical-professionals/${mp.id}`}
                                variant="text"
                                size="small"
                              >
                                {mp.user?.full_name || "N/A"}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={mp.mp_category || "N/A"}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {mp.medical_license_number || "N/A"}
                            </TableCell>
                            <TableCell>{mp.vehicle_number || "N/A"}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body1" color="text.secondary">
                              No medical professionals found for this facility
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={stats.total_medical_professionals || 0}
                    rowsPerPage={rowsPerPage}
                    page={mpsPage}
                    onPageChange={(e, newPage) => setMpsPage(newPage)}
                    rowsPerPageOptions={[10]}
                  />
                </TableContainer>
              )}
            </Box>
          )}

          {/* Orders Tab */}
          {activeTab === 3 && (
            <Box>
              {loadingOrders ? (
                <LinearProgress />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.length > 0 ? (
                        orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>
                              <Button
                                component={Link}
                                to={`/orders/${order.id}`}
                                variant="text"
                                size="small"
                              >
                                {order.order_number || `Order ${order.id}`}
                              </Button>
                            </TableCell>
                            <TableCell>
                              {order.patient?.full_name || "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status || "N/A"}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(order.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body1" color="text.secondary">
                              No orders found for this facility
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={stats.total_orders || 0}
                    rowsPerPage={rowsPerPage}
                    page={ordersPage}
                    onPageChange={(e, newPage) => setOrdersPage(newPage)}
                    rowsPerPageOptions={[10]}
                  />
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default FacilityDetail;
